import { getFeatureFlagValue } from '@citrite/feature-flags';
import { configureSolutionsClient } from '@data/clients';
import { getDefaultModalProps } from '@overlays/getDefaultOverlayOptions';
import { SolutionInstallationsModal } from '@overlays/SolutionInstallationsModal';
import { appshellMenuExtensionSlots } from '@sharefiledev/appshell-menu-pilet-sdk';
import {
	packagesDashboardExtensionSlots,
	rubiconPiletOverlays,
} from '@sharefiledev/rubicon-pilet-sdk';
import {
	PageComponentProps,
	PiletApi,
	PiletUserContext,
} from '@sharefiledev/sharefile-appshell';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { addBlocks } from './blocks';
import {
	integrationsEntitlement,
	solutionsEntitlement,
	templatesEntitlement,
} from './entitlements';
import {
	AsyncLazyNavigationButton,
	AsyncLazyPublisherProfileContent,
	AsyncLazyRequestInstallModal,
} from './Extensions';
import { NavigationMenuItem } from './Extensions/NavigationMenuItem';
import { FeatureFlag } from './FeatureFlags';
import { setLogger } from './logger';
import { AsyncCatalogBrowseByTypePage } from './Pages';
import {
	checkRolesAndPreferences,
	RolesAndProvisioningRequirements,
} from './provisioning';
import { basePath, catalogBrowseTypePath, publisherProfilePath } from './routes';

const queryClient = new QueryClient();

/**
 * Helper to get pilet roles and provisioning requirements,
 * taking into consideration any feature flags for a ggiven account.
 * @returns Roles and provisioning requirements for the Pilet
 */
function getRolesAndProvisioningReqs(): RolesAndProvisioningRequirements {
	const isPLG = getFeatureFlagValue(FeatureFlag.EnableProductLedGrowth);
	return {
		requiredRoles: ['Employee'],
		requiredASTs: isPLG ? undefined : ['EnableCatalog'],
	} as RolesAndProvisioningRequirements;
}

/**
 * Checks to see if a user is permitted to access the Pilet
 * @param app Pilet API
 * @returns true if permitted to access Pilet, false if not
 */
function checkIsPermitted(app: PiletApi): boolean {
	// Check if user has sufficient roles and preferences
	if (checkRolesAndPreferences(app, getRolesAndProvisioningReqs())) {
		if (getFeatureFlagValue(FeatureFlag.EnableProductLedGrowth)) {
			// If the user is PLG, next thing to do is to check entitlements.
			const { entitlements }: PiletUserContext = app.sf.piletUserContext.get();
			if (
				!entitlements.getEntitlementStatus(integrationsEntitlement) &&
				!entitlements.getEntitlementStatus(solutionsEntitlement) &&
				!entitlements.getEntitlementStatus(templatesEntitlement)
			) {
				// If entitlements are bad for PLG user, return false (NOT PERMITTED)
				return false;
			}
		}

		// If you get to this point, return true (PERMITTED)
		return true;
	} else {
		// If the user does not have sufficient roles and prefs, return false (NOT PERMITTED)
		return false;
	}
}

/**
 * Sets up the Catalog Pilet, registering the necessary
 * components and extensions needed for the Pilet.
 * @param app Pilet API
 */
export function setup(app: PiletApi) {
	(window as any).SF_rubiconpiletexecuted = true;

	if (!checkIsPermitted(app)) {
		return;
	}

	setLogger(app.sf.getLogger());
	configureApiClients(app);
	registerPages(app);
	registerOverlays(app);
	registerExtensions(app);
	addBlocks(app);
}

function registerExtensions(app: PiletApi) {
	app.registerExtension(
		packagesDashboardExtensionSlots.catalogNavigationButton,
		({ params }) => {
			return <AsyncLazyNavigationButton basePath={basePath} {...params} />;
		}
	);
	app.registerExtension(appshellMenuExtensionSlots.userMenuItem, ({ params }) => (
		<NavigationMenuItem {...params} />
	));
}

function configureApiClients(app: PiletApi) {
	configureSolutionsClient(app);
}

function registerOverlays(app: PiletApi) {
	registerRequestInstallModal(app);
	registerSolutionInstallationsModal(app);
}

function registerRequestInstallModal(app: PiletApi) {
	app.registerModal(
		rubiconPiletOverlays.requestInstallModal,
		props => (
			<QueryClientProvider client={queryClient} contextSharing>
				<AsyncLazyRequestInstallModal {...props} piletApi={app} />
			</QueryClientProvider>
		),
		getDefaultModalProps({
			className: 'modal-request-solution-install',
		})
	);
}

function registerSolutionInstallationsModal(app: PiletApi) {
	app.registerModal(
		rubiconPiletOverlays.solutionInstallationsModal,
		props => <SolutionInstallationsModal {...props} piral={app} />,
		getDefaultModalProps({
			className: 'modal-solution-installations',
			isFullscreen: true,
		})
	);
}

function registerPages(app: PiletApi) {
	registerBasePathPage(app);
	registerPublisherProfilePage(app);
	registerCatalogBrowsePage(app); // Needs to happen last
}

// Register `/catalog` route
function registerBasePathPage(app: PiletApi) {
	app.registerPage(basePath, () => <AsyncCatalogBrowseByTypePage app={app} />, {
		pageType: 'solutionsCatalog',
	});
}

// Register `/catalog/publisher?namespace={namespace}` route
function registerPublisherProfilePage(app: PiletApi) {
	const PublisherProfileShell = (_props: PageComponentProps) => (
		<QueryClientProvider client={queryClient} contextSharing>
			<AsyncLazyPublisherProfileContent basePath={basePath} piletApi={app} />
		</QueryClientProvider>
	);

	app.registerPage(publisherProfilePath, PublisherProfileShell, {
		pageType: 'solutionsCatalog',
	});
}

// Register `/catalog/:type` route
function registerCatalogBrowsePage(app: PiletApi) {
	app.registerPage(
		catalogBrowseTypePath,
		() => <AsyncCatalogBrowseByTypePage app={app} />,
		{
			pageType: 'solutionsCatalog',
		}
	);
}
